import { SxProps as MuiSxProps, Theme } from '@mui/material';
import { SystemCssProperties, ResponsiveStyleValue, AllSystemCSSProperties } from '@mui/system';

export type SxProps = MuiSxProps<Theme>;

/**
 * Utility type for providing a limited set of style properties that are compatible with the `sx` API. This is helpful
 * for exposing a limited set of "safe" CSS features on a component to avoid the down sides of exposing an open-ended
 * `sx` prop.
 */
export type LimitedSxProps<K extends keyof SystemCssProperties> = {
  // This mapped type is similar to the behavior in MUI's `SystemCssProperties`, but with just the set of supported
  // CSS properties instead of all of them.
  [Property in K]?:
    | ResponsiveStyleValue<AllSystemCSSProperties[Property]>
    | ((theme: Theme) => ResponsiveStyleValue<AllSystemCSSProperties[Property]>);
};

/**
 * Combines sx props from multiple sources. Useful when creating a component and
 * you want consumers to be able to provide SxProps that should be combined with
 * some built-in styles.
 */
export function combineSx(...sxProps: Array<SxProps | undefined>): SxProps {
  return sxProps.flatMap((sx) => {
    if (sx === undefined) return [];
    if (Array.isArray(sx)) return sx;
    return [sx];
  });
}
